import type { SxProps, Theme } from '@mui/material/styles';
import type { DOMNode, Element } from 'html-react-parser';

import {
  isMaterialIconTag,
  materialIconIdFromAttr,
} from 'src/common/components/icons/materialIconRegistry';
import { parseTinyMceInlineStyle } from 'src/helpers/tinymceTableStyles';

/** Optional list-item icon id (use with Icon selector or `<icon icon="…">` as first child of `li`). */
export const LIST_ITEM_ICON_ATTR = 'data-ttt-list-icon';

const TEXT_ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify', 'start', 'end']);

function isListElement(node: DOMNode): node is Element {
  return node.type === 'tag' && (node.name === 'ul' || node.name === 'ol');
}

function isIconElement(node: DOMNode): node is Element {
  return node.type === 'tag' && node.name === 'icon';
}

type PartitionedListItemChildren = {
  iconId?: string;
  materialIconTag?: string;
  iconAttribs?: Record<string, string>;
  nestedLists: Element[];
  contentNodes: DOMNode[];
};

export function partitionListItemChildren(
  children: DOMNode[],
  liAttribs: Record<string, string>,
): PartitionedListItemChildren {
  const nestedLists: Element[] = [];
  const contentNodes: DOMNode[] = [];
  let iconId = liAttribs[LIST_ITEM_ICON_ATTR]?.trim() || undefined;
  let materialIconTag: string | undefined;
  let iconAttribs: Record<string, string> | undefined;

  for (const child of children) {
    if (isIconElement(child) && child.attribs?.icon) {
      if (!iconId && !materialIconTag) {
        const rawIcon = child.attribs.icon.trim();
        const materialId = materialIconIdFromAttr(rawIcon);
        if (materialId) {
          materialIconTag = `${materialId}Icon`; // tag name for renderMaterialIconNode
        } else {
          iconId = rawIcon;
        }
        iconAttribs = child.attribs;
      }
      continue;
    }
    if (child.type === 'tag' && isMaterialIconTag(child.name)) {
      if (!iconId && !materialIconTag) {
        materialIconTag = child.name;
        iconAttribs = child.attribs;
      }
      continue;
    }
    if (isListElement(child)) {
      nestedLists.push(child);
      continue;
    }
    contentNodes.push(child);
  }

  return { iconId, materialIconTag, iconAttribs, nestedLists, contentNodes };
}

export const richTextListOuterSx: SxProps<Theme> = {
  mb: 2,
  width: '100%',
  '&:last-child': { mb: 0 },
};

export function richTextListRootSx(ordered = false): SxProps<Theme> {
  return {
    width: '100%',
    py: 0,
    pl: 0,
    listStyle: 'none',
    color: 'text.primary',
    ...(ordered ? { counterReset: 'ttt-rich-text-ol' } : {}),
  };
}

export const RICH_TEXT_LIST_ITEM_ICON_CLASS = 'ttt-list-item--icon';

export const richTextNestedListSx: SxProps<Theme> = {
  width: '100%',
  py: 0.5,
  pl: 2.5,
  mt: 0.5,
};

export function richTextListItemSx(
  attribs: Record<string, string>,
  options: { hasIcon: boolean; ordered: boolean },
): SxProps<Theme> {
  const inline = parseTinyMceInlineStyle(attribs.style);
  const align = attribs.align?.toLowerCase();
  const textAlign =
    inline.textAlign ??
    (align && TEXT_ALIGN_VALUES.has(align)
      ? (align as unknown as typeof inline.textAlign)
      : undefined);

  const shared = {
    py: 0.5,
    px: 0,
    ...(textAlign ? { textAlign } : {}),
  };

  if (options.hasIcon) {
    return {
      ...shared,
      display: 'flex',
      alignItems: 'flex-start',
      listStyle: 'none',
    };
  }

  return {
    ...shared,
    display: 'list-item',
    listStyleType: options.ordered ? 'decimal' : 'disc',
    listStylePosition: 'outside',
    ml: 2.5,
  };
}

export function listSxFromAttribs(attribs: Record<string, string>): SxProps<Theme> {
  const inline = parseTinyMceInlineStyle(attribs.style);
  return {
    ...(inline.textAlign ? { textAlign: inline.textAlign } : {}),
  };
}
